import { useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useBranch } from "@/context/BranchContext";
import { MovementDialog } from "@/components/MovementDialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import {
  LayoutDashboard, Package, Boxes, ArrowLeftRight, Truck, ShoppingCart,
  Trash2, BarChart3, Users, Building2, LogOut, ChevronLeft, ChevronRight, Plus, Menu, ClipboardCheck,
} from "lucide-react";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/stock", label: "Stock", icon: Boxes },
  { to: "/stock-counts", label: "Stock Counts", icon: ClipboardCheck },
  { to: "/items", label: "Items", icon: Package },
  { to: "/transfers", label: "Transfers", icon: ArrowLeftRight },
  { to: "/purchase-orders", label: "Purchase Orders", icon: ShoppingCart },
  { to: "/suppliers", label: "Suppliers", icon: Truck },
  { to: "/wastage", label: "Wastage", icon: Trash2 },
  { to: "/reports", label: "Reports", icon: BarChart3 },
  { to: "/branches", label: "Branches", icon: Building2 },
  { to: "/staff", label: "Staff", icon: Users, ownerOnly: true },
];

export default function AppShell() {
  const { auth, logout } = useAuth();
  const { branches, selected, select, role } = useBranch();
  const [collapsed, setCollapsed] = useState(false);
  const [dialog, setDialog] = useState(null); // "record" | "wastage"
  const [mobileMenu, setMobileMenu] = useState(false);
  const navigate = useNavigate();

  const nav = NAV.filter((n) => !n.ownerOnly || role === "owner");

  const navLinks = (onClick) =>
    nav.map((n) => (
      <NavLink key={n.to} to={n.to} onClick={onClick} data-testid={`nav-${n.to.slice(1)}`}
        className={({ isActive }) =>
          `flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
            isActive ? "bg-primary text-primary-foreground" : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
          }`}>
        <n.icon className="h-4 w-4 shrink-0" />
        {!collapsed && <span>{n.label}</span>}
      </NavLink>
    ));

  return (
    <div className="min-h-screen bg-background flex">
      <aside className={`hidden lg:flex flex-col border-r bg-white sticky top-0 h-screen transition-[width] duration-200 ${collapsed ? "w-16" : "w-60"}`}>
        <div className="h-16 flex items-center gap-2 px-4 border-b font-heading font-extrabold tracking-tight">
          <Boxes className="h-6 w-6 text-primary shrink-0" />
          {!collapsed && <span>StockPilot PH</span>}
        </div>
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">{navLinks()}</nav>
        <button onClick={() => setCollapsed(!collapsed)} data-testid="sidebar-collapse-btn"
          className="h-12 border-t flex items-center justify-center text-slate-400 hover:text-slate-700">
          {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
        </button>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 border-b bg-white sticky top-0 z-20 flex items-center gap-3 px-4 sm:px-6">
          <button className="lg:hidden text-slate-600" onClick={() => setMobileMenu(!mobileMenu)} data-testid="mobile-menu-btn">
            <Menu className="h-5 w-5" />
          </button>
          <div className="flex-1 min-w-0">
            <Select value={selected} onValueChange={select}>
              <SelectTrigger className="w-full sm:w-64 bg-white" data-testid="branch-switcher">
                <Building2 className="h-4 w-4 mr-2 text-primary shrink-0" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {role === "owner" && <SelectItem value="all">All branches</SelectItem>}
                {branches.map((b) => <SelectItem key={b.branch_id} value={b.branch_id}>{b.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="hidden md:flex gap-2">
            <Button size="sm" onClick={() => setDialog("record")} data-testid="quick-record-stock-btn">
              <Plus className="h-4 w-4 mr-1" /> Record stock
            </Button>
            <Button size="sm" variant="outline" onClick={() => setDialog("wastage")} data-testid="quick-log-wastage-btn">
              <Trash2 className="h-4 w-4 mr-1" /> Log wastage
            </Button>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="user-menu-btn" className="h-9 w-9 rounded-full bg-primary text-white font-semibold text-sm flex items-center justify-center shrink-0">
                {auth?.user?.name?.[0]?.toUpperCase() || "U"}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>
                <div className="text-sm font-medium">{auth?.user?.name}</div>
                <div className="text-xs text-muted-foreground">{auth?.user?.email} · {role}</div>
                <div className="text-xs text-muted-foreground">{auth?.company?.name}</div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={async () => { await logout(); navigate("/"); }} data-testid="logout-btn">
                <LogOut className="h-4 w-4 mr-2" /> Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>

        {mobileMenu && (
          <div className="lg:hidden border-b bg-white p-3 grid grid-cols-2 gap-1 z-10" data-testid="mobile-nav">
            {navLinks(() => setMobileMenu(false))}
          </div>
        )}

        <main className="flex-1 p-4 sm:p-6 pb-24 lg:pb-6 max-w-screen-2xl w-full mx-auto fade-in">
          <Outlet />
        </main>

        <div className="lg:hidden fixed bottom-0 inset-x-0 bg-white border-t z-30 flex items-center justify-around py-2 pb-[max(0.5rem,env(safe-area-inset-bottom))]">
          <NavLink to="/dashboard" className="flex flex-col items-center text-[11px] text-slate-600 gap-0.5" data-testid="bottom-nav-dashboard">
            <LayoutDashboard className="h-5 w-5" /> Home
          </NavLink>
          <NavLink to="/stock" className="flex flex-col items-center text-[11px] text-slate-600 gap-0.5" data-testid="bottom-nav-stock">
            <Boxes className="h-5 w-5" /> Stock
          </NavLink>
          <button onClick={() => setDialog("record")} data-testid="bottom-nav-record"
            className="h-12 w-12 -mt-6 rounded-full bg-primary text-white flex items-center justify-center shadow-lg active:scale-95">
            <Plus className="h-6 w-6" />
          </button>
          <button onClick={() => setDialog("wastage")} className="flex flex-col items-center text-[11px] text-slate-600 gap-0.5" data-testid="bottom-nav-wastage">
            <Trash2 className="h-5 w-5" /> Wastage
          </button>
          <NavLink to="/reports" className="flex flex-col items-center text-[11px] text-slate-600 gap-0.5" data-testid="bottom-nav-reports">
            <BarChart3 className="h-5 w-5" /> Reports
          </NavLink>
        </div>
      </div>

      <MovementDialog open={dialog === "record"} onOpenChange={(o) => !o && setDialog(null)} defaultType="receive" />
      <MovementDialog open={dialog === "wastage"} onOpenChange={(o) => !o && setDialog(null)} defaultType="wastage" lockType />
    </div>
  );
}
